import { useEffect, useState } from "react";
import axios from "axios";
import * as Icons from "lucide-react";
import { PUBLIC_BASE } from "../lib/api";

/**
 * Horizontal scrollable strip of the realtor's other listings — shown on
 * both the share page (sibling listings) and the dashboard (full portfolio).
 * Hides itself entirely when there are <= 0 other listings, so a
 * single-listing realtor doesn't get a sad empty strip.
 *
 * Modes:
 *   token={dashboardToken}                  → all listings of the realtor
 *   listingId={...}                         → siblings only (excludes current)
 */
export default function OtherListingsStrip({ token, listingId, brand, title }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const url = listingId
      ? `${PUBLIC_BASE}/api/share/listings/${listingId}/sibling-listings`
      : token
        ? `${PUBLIC_BASE}/api/share/realtors/${token}/other-listings`
        : null;
    if (!url) {
      setLoading(false);
      return;
    }
    axios
      .get(url)
      .then((r) => {
        if (!cancelled) setItems(r.data || []);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, listingId]);

  if (loading || items.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="other-listings-strip">
      <div className="flex items-center gap-2">
        <Icons.LayoutGrid size={14} style={{ color: brand || "#a1a1aa" }} />
        <div className="text-[10px] uppercase tracking-[0.4em]" style={{ color: brand || "#71717a" }}>
          {title || "Also represented"}
        </div>
        <div className="text-[10px] text-zinc-600 font-mono">· {items.length}</div>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
        {items.map((it) => {
          const link = it.reel_share_token
            ? `/share/${it.reel_share_token}`
            : null;
          const card = (
            <>
              <div className="aspect-[4/3] bg-zinc-900 rounded-xl overflow-hidden relative">
                {it.cover_image_url ? (
                  <img
                    src={`${PUBLIC_BASE}${it.cover_image_url}`}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Icons.Home size={28} className="text-zinc-700" />
                  </div>
                )}
                {it.is_sold && (
                  <div className="absolute top-2 left-2 bg-rose-500/90 text-white text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded">
                    Sold
                  </div>
                )}
              </div>
              <div className="mt-2 text-sm text-white font-medium truncate">
                {it.address || "Listing"}
              </div>
              <div className="text-[11px] text-zinc-500 truncate">
                {[it.city, it.country].filter(Boolean).join(", ")}
                {it.price && (
                  <span className="text-zinc-300 font-medium ml-2">
                    {it.price}
                  </span>
                )}
              </div>
            </>
          );
          return link ? (
            <a
              key={it.id}
              href={link}
              className="shrink-0 w-56 snap-start group hover:opacity-90 transition-opacity"
              data-testid={`other-listing-${it.id}`}
            >
              {card}
            </a>
          ) : (
            <div
              key={it.id}
              className="shrink-0 w-56 snap-start opacity-70"
              data-testid={`other-listing-${it.id}`}
            >
              {card}
            </div>
          );
        })}
      </div>
    </div>
  );
}
